import flagsmith from '@flagsmith/flagsmith';
import type { IInitConfig } from '@flagsmith/flagsmith';
import { DEFAULT_FLAGS } from './featureFlagKeys';
import type { FeatureFlagKey } from './featureFlagKeys';

/**
 * The single Flagsmith instance for the app. `FeatureFlagsProvider` hands it to the
 * Flagsmith React provider, and every hook reads through that provider rather than
 * importing this instance directly, so tests can swap in their own instance.
 */
export const flagsmithClient = flagsmith;

export type FlagsmithClient = typeof flagsmith;

/** How long we wait for Flagsmith before letting the app through with flags off. */
export const FLAG_FETCH_TIMEOUT_MS = 5000;

export const FLAGS_UNAVAILABLE_MESSAGE =
  'Some features are currently unavailable, but the rest of the page is working normally.';

/**
 * Read lazily rather than at module load: the value is injected at build time per
 * environment, and tests stub it per case.
 */
export function getFlagsmithEnvironmentId(): string | undefined {
  return process.env.REACT_APP_FLAGSMITH_ENVIRONMENT_ID;
}

/**
 * Passing `identity` to `init` fetches that user's flags in the very first request.
 * Initialising without it would fetch the environment's anonymous flags, then throw them
 * away and fetch again once we identified the user, so this saves a whole round trip.
 */
export function buildInitConfig(environmentID: string, identity: string): IInitConfig<FeatureFlagKey> {
  return {
    environmentID,
    identity,
    defaultFlags: DEFAULT_FLAGS,
    // Keep the last known flags in local storage. Flagsmith still refreshes from the API
    // on every init, so the cache is only read while that request is in flight or when it
    // fails - which is exactly when we would otherwise turn every feature off.
    cacheFlags: true,
  };
}
